import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { connect } from "react-redux";
import throttle from "lodash/throttle";
import ReplaceableField from "./ReplaceableField";
import AssetPreview from "./AssetPreview";
import LuciPanel from "./LuciPanel";
import LuciPropTypes from "../../propTypes";
import { actions as assetActions } from "../../actions/asset";
import { makeGetConfigValue } from "../../selectors/config";
import { isAutofillFillSelector } from "../../util/autofillUtils";
import { cleanImageUrl } from "../../util/urlUtils";
import MessageType from "../../constants/luciPanelMessageTypes";

const ReplaceablesContainer = styled.div`
  height: ${props => props.height || "100%"};
  overflow-y: hidden;
  display: flex;
  flex-direction: ${props => (props.vertical ? "column" : "row")};
  overflow-x: auto;
  flex: 1 1 auto;
`;

const ReplaceableContent = styled.div`
  user-drag: none;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  display: ${props => (props.hide ? "none" : "block")};
  box-sizing: border-box;
  padding: 0.5rem;
  background-color: ${props => props.theme.background};
  height: 100%;
  flex-basis: 30%;
  flex: ${props => (props.columnsResized ? "0 1" : "1")};
  overflow-y: auto;
  &:not(:last-child) {
    border-right: ${props => (props.noBorder ? "0" : "1px solid grey")};
  }

  &:last-child {
    border-top: ${props => (props.borderTop ? "1px solid grey" : "0")};
  }
`;

const Handle = styled.div`
  width: 5px;
  padding: 0;
  cursor: ew-resize;
  flex: 0 0 auto;
  background-color: ${props => props.theme.background}

  &::before {
    content: '';
    display: block;
    width: 1px;
    height: 100%;
    background: grey;
    margin: 0 auto;
  }
`;

class AssetEditor extends React.Component {
  constructor() {
    super();

    this.state = {
      textColumnWidth: null,
      mediaColumnWidth: null,
      previewColumnWidth: null,
      columnsResized: false
    };
    this.dataPanelInitialized = false;
    this.replaceableHandler = null;
    this.previewHandle = null;

    this.textReplaceableColumn = null;
    this.mediaReplaceableColumn = null;
    this.assetPreviewColumn = null;

    this.throttledMouseMoveHandler = throttle(this.handleMouseMove, 50);
    this.throttledResizeHandler = throttle(this.handleResize, 100);

    this.activeHandle = null;
  }

  UNSAFE_componentWillMount() {
    if (!this.props.verticalLayout) {
      window.addEventListener("resize", this.throttledResizeHandler);
      if (window.PointerEvent) {
        document.addEventListener("pointerdown", this.handleMouseDown);
        document.addEventListener(
          "pointermove",
          this.throttledMouseMoveHandler
        );
        document.addEventListener("pointerup", this.handleMouseUp);
      } else {
        document.addEventListener("mousedown", this.handleMouseDown);
        document.addEventListener("mousemove", this.throttledMouseMoveHandler);
        document.addEventListener("mouseup", this.handleMouseUp);
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const currentReplaceables = this.props.column1Replaceables.concat(
      this.props.column2Replaceables
    );
    const newReplaceables = nextProps.column1Replaceables.concat(
      nextProps.column2Replaceables
    );
    const previewGenerated = this.props.previewImg !== nextProps.previewImg;
    const columnsChanged =
      this.state.mediaColumnWidth !== nextState.mediaColumnWidth ||
      this.state.textColumnWidth !== nextState.textColumnWidth;
    return (
      this.replaceablesUpdated(currentReplaceables, newReplaceables) ||
      (this.props.autofillData && !nextProps.autofillData) ||
      previewGenerated ||
      columnsChanged ||
      this.state.columnsResized !== nextState.columnsResized ||
      this.props.primeStyle !== nextProps.primeStyle
    );
  }

  UNSAFE_componentWillUpdate(nextProps) {
    const currentReplaceables = this.props.column1Replaceables.concat(
      this.props.column2Replaceables
    );
    const newReplaceables = nextProps.column1Replaceables.concat(
      nextProps.column2Replaceables
    );
    const previewGenerated = this.props.previewImg !== nextProps.previewImg;
    if (this.dataPanel && this.dataPanelInitialized) {
      if (this.replaceablesUpdated(currentReplaceables, newReplaceables)) {
        const replaceables = nextProps.column1Replaceables.concat(
          nextProps.column2Replaceables
        );
        this.sendReplaceablesToPanel(
          MessageType.LUCI.UPDATED.REQUEST,
          replaceables
        );
      }
      if (previewGenerated) {
        this.sendPreviewToPanel(nextProps.previewImg);
      }
    }

    if (!nextProps.templateSwapping) {
      const forceAutoPreview = newReplaceables.some(
        r => r.properties && !!r.properties.usesDataSource
      );
      if (
        (nextProps.autoPreviewEnabled || forceAutoPreview) &&
        (this.replaceablesUpdated(currentReplaceables, newReplaceables) ||
          this.props.primeStyle !== nextProps.primeStyle)
      ) {
        this.props.generatePreview();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.throttledResizeHandler);
    if (window.PointerEvent) {
      document.removeEventListener("pointerdown", this.handleMouseDown);
      document.removeEventListener(
        "pointermove",
        this.throttledMouseMoveHandler
      );
      document.removeEventListener("pointerup", this.handleMouseUp);
    } else {
      document.removeEventListener("mousedown", this.handleMouseDown);
      document.removeEventListener("mousemove", this.throttledMouseMoveHandler);
      document.removeEventListener("mouseup", this.handleMouseUp);
    }
  }

  getAutofillList = (autofillData, name, index) => {
    if (autofillData && autofillData[name]) {
      return autofillData[name].map(a => a.columns[index].value) || [];
    }
  };

  getAutofillIndex = (value, name) => {
    if (this.props.autofillData && this.props.autofillData[name]) {
      this.props.autofillData[name].findIndex(a =>
        a.columns.some(c => c.value === value)
      );
    }
  };

  handleMouseDown = e => {
    if (
      (!!this.replaceableHandle && e.target === this.replaceableHandle) ||
      e.target === this.previewHandle
    ) {
      this.activeHandle = e.target;
      this.isDragging = true;
    }
  };

  handleMouseUp = () => {
    this.activeHandle = null;
    this.isDragging = false;
  };

  handleMouseMove = e => {
    if (!this.isDragging || !this.activeHandle) {
      return false;
    }

    let textColumnWidth = this.state.textColumnWidth || 0;
    let mediaColumnWidth = this.state.mediaColumnWidth || 0;
    let previewColumnWidth = this.state.previewColumnWidth || 0;

    if (!!this.replaceableHandle) {
      if (this.activeHandle === this.replaceableHandle &&
        e.clientX > 100 &&
        e.clientX < this.previewHandle.offsetLeft - 100) {
        textColumnWidth = e.clientX;
        mediaColumnWidth =
          this.previewHandle.offsetLeft -
          e.clientX -
          this.replaceableHandle.offsetWidth;
        previewColumnWidth = this.assetPreviewColumn.offsetWidth;
      } else if (this.activeHandle === this.previewHandle &&
        e.clientX > this.replaceableHandle.offsetLeft + this.replaceableHandle.offsetWidth + 100 &&
        e.clientX < this.container.offsetWidth) {
        textColumnWidth = this.replaceableHandle.offsetLeft;
        mediaColumnWidth = e.clientX - textColumnWidth - 10;
        previewColumnWidth = this.container.offsetWidth - e.clientX;
      }
    } else if (this.activeHandle === this.previewHandle &&
      e.clientX > 100 && e.clientX < this.container.offsetWidth) {
      textColumnWidth = e.clientX;
      mediaColumnWidth = 0;
      previewColumnWidth = this.container.offsetWidth - e.clientX;
    }

    textColumnWidth = Math.max(textColumnWidth, 100);
    mediaColumnWidth = Math.max(mediaColumnWidth, 100);
    previewColumnWidth = Math.max(previewColumnWidth, 100);

    this.setState({
      textColumnWidth,
      mediaColumnWidth,
      previewColumnWidth,
      columnsResized: true
    });
  };

  handleResize = () => {
    // Reset columns if a resize happens.
    this.setState({ columnsResized: false });
  };

  replaceablesUpdated = (oldReplaceables, newReplaceables) => {
    if (oldReplaceables.length === 0 && newReplaceables.length > 0) {
      return true;
    }
    for (let i = 0; i < oldReplaceables.length; i++) {
      const x = oldReplaceables[i];
      const y = newReplaceables.find(r => r.id === x.id);
      if (y != null) {
        if (x.value !== y.value) {
          return true;
        }

        if (x.url !== y.url) {
          const trimmedXUrl = x.url ? x.url.split("?")[0] : x.url;
          const trimmedYUrl = y.url ? y.url.split("?")[0] : y.url;
          if (trimmedXUrl !== trimmedYUrl) {
            return true;
          }
        }
      }
    }
    return false;
  };

  updateReplaceableValuesFromDataPanel = data => {
    if (data && data.data && data.data.replaceables) {
      data.data.replaceables.forEach(r => {
        const replaceableId = r.id;
        const replaceables = this.props.column1Replaceables.concat(
          this.props.column2Replaceables
        );
        const replaceable = replaceables.find(
          repl => repl.id === replaceableId
        );
        if (
          replaceableId &&
          replaceable &&
          replaceable.properties &&
          r.value &&
          r.value !== replaceable.value
        ) {
          if (replaceable.properties["data list xpath"]) {
            const autofillIndex = this.getAutofillIndex(
              r.value,
              replaceable.properties.displayName
            );
            if (autofillIndex >= 0) {
              this.props.updateAutofillValues(
                autofillIndex,
                replaceable.properties.displayName
              );
            } else {
              this.props.updateReplaceableValue({
                ...replaceable,
                id: replaceableId,
                url: r.thumbUrl,
                value: r.value
              });
            }
          } else {
            this.props.updateReplaceableValue({
              ...replaceable,
              id: replaceableId,
              url: r.thumbUrl,
              value: r.value
            });
          }
        }
      });
    }
  };

  openDialogFromDataPanel = data => {
    const replaceableId = data.data.id || "";
    const mediaReplaceables = this.props.column1Replaceables
      .concat(this.props.column2Replaceables)
      .filter(r => r.type !== "text");
    const replaceable = mediaReplaceables.find(x => replaceableId === x.id);
    if (replaceable) {
      this.props.selectReplaceable(
        replaceableId,
        replaceable.properties.assetCategory,
        replaceable.properties.restrictAssetCategory
      );
      this.props.openDialog(
        replaceable.type,
        replaceable.properties.assetCategory,
        replaceable.properties.restrictAssetCategory
      );
    }
  };

  sendReplaceablesToPanel = (type, repl) => {
    const replaceables =
      repl ||
      this.props.column1Replaceables.concat(this.props.column2Replaceables);
    this.dataPanel.sendMessage(
      {
        mosId: null,
        replaceables: replaceables.map(r => {
          const replaceableData = {
            id: r.id,
            thumbUrl: cleanImageUrl(r.url),
            type: r.type,
            name: r.name,
            value: r.value
          };
          if (
            (r.properties["data list xpath"] ||
              r.properties.autofillDataListName) &&
            this.props.autofillData &&
            this.props.autofillData[r.properties.displayName]
          ) {
            replaceableData.dataList = this.getAutofillList(
              this.props.autofillData,
              r.properties.displayName,
              r.properties.autofillDataColumnIndex
            );
          }
          return replaceableData;
        })
      },
      type
    );
  };

  sendPreviewToPanel = preview => {
    this.dataPanel.sendMessage(
      {
        previewImg: preview
      },
      MessageType.LUCI.PREVIEWGENERATED.REQUEST
    );
  };

  countValidReplaceables = replaceables => {
    if (!replaceables || replaceables.length === 0) {
      return 0;
    }
    return replaceables.filter(x => !x.properties["read only"]).length;
  };

  renderCustomPanelView() {
    const customPanelSrc = this.props.customPanel.url;

    return (
      <ReplaceablesContainer vertical={this.props.verticalLayout}>
        <ReplaceableContent noBorder={this.props.verticalLayout}>
          <LuciPanel
            src={customPanelSrc}
            height="100%"
            width="100%"
            onSelectImageRequest={this.openDialogFromDataPanel}
            onRef={ref => (this.dataPanel = ref)}
            onDataPanelLoad={() => {
              this.sendReplaceablesToPanel(MessageType.DATAPANEL.LOAD.RESPONSE);
              this.dataPanelInitialized = true;
            }}
            onDataPanelUpdate={this.updateReplaceableValuesFromDataPanel}
          />
        </ReplaceableContent>
        <ReplaceableContent borderTop={this.props.verticalLayout}>
          <AssetPreview />
        </ReplaceableContent>
      </ReplaceablesContainer>
    );
  }

  renderVerticalView() {
    const {
      column1Replaceables,
      column2Replaceables,
      openDialog,
      updateReplaceableValue,
      updateAutofillValues
    } = this.props;

    return (
      <ReplaceablesContainer vertical>
        <ReplaceablesContainer height="50%">
          <ReplaceableContent
            hide={this.countValidReplaceables(column1Replaceables) === 0}
          >
            {column1Replaceables.map(replaceable => {
              if (!replaceable.properties["read only"]) {
                return (
                  <ReplaceableField
                    key={replaceable.id}
                    replaceableId={replaceable.id}
                    onClick={
                      replaceable.type !== "text" ? openDialog : () => {}
                    }
                    updateReplaceableValue={
                      isAutofillFillSelector(replaceable)
                        ? updateAutofillValues
                        : updateReplaceableValue
                    }
                  />
                );
              }
              return "";
            })}
          </ReplaceableContent>
          <ReplaceableContent
            hide={this.countValidReplaceables(column2Replaceables) === 0}
          >
            {column2Replaceables.map(replaceable => {
              if (!replaceable.properties["read only"]) {
                return (
                  <ReplaceableField
                    key={replaceable.id}
                    replaceableId={replaceable.id}
                    onClick={
                      replaceable.type !== "text" ? openDialog : () => {}
                    }
                    updateReplaceableValue={
                      isAutofillFillSelector(replaceable)
                        ? updateAutofillValues
                        : updateReplaceableValue
                    }
                  />
                );
              }
              return "";
            })}
          </ReplaceableContent>
        </ReplaceablesContainer>
        <ReplaceableContent borderTop>
          <AssetPreview />
        </ReplaceableContent>
      </ReplaceablesContainer>
    );
  }

  renderDefaultView() {
    const {
      column1Replaceables,
      column2Replaceables,
      openDialog,
      updateReplaceableValue,
      updateAutofillValues
    } = this.props;

    const {
      textColumnWidth,
      mediaColumnWidth,
      previewColumnWidth,
      columnsResized
    } = this.state;

    return (
      <ReplaceablesContainer ref={ref => (this.container = ref)}>
        <ReplaceableContent
          style={{
            minWidth:
              textColumnWidth && columnsResized
                ? `${textColumnWidth}px`
                : "100px"
          }}
          columnsResized={columnsResized}
          noBorder
          hide={this.countValidReplaceables(column1Replaceables) === 0}
          ref={ref => (this.textReplaceableColumn = ref)}
        >
          {column1Replaceables.map(replaceable => {
            if (!replaceable.properties["read only"]) {
              return (
                <ReplaceableField
                  key={replaceable.id}
                  replaceableId={replaceable.id}
                  onClick={replaceable.type !== "text" ? openDialog : () => {}}
                  updateReplaceableValue={
                    isAutofillFillSelector(replaceable)
                      ? updateAutofillValues
                      : updateReplaceableValue
                  }
                />
              );
            }
            return "";
          })}
        </ReplaceableContent>
        {!!this.props.column2Replaceables && this.props.column2Replaceables.length > 0 &&
          <Handle ref={ref => (this.replaceableHandle = ref)} /> }
        <ReplaceableContent
          style={{
            minWidth:
              mediaColumnWidth && columnsResized
                ? `${mediaColumnWidth}px`
                : "100px"
          }}
          columnsResized={columnsResized}
          noBorder
          hide={this.countValidReplaceables(column2Replaceables) === 0}
          ref={ref => (this.mediaReplaceableColumn = ref)}
        >
          {column2Replaceables.map(replaceable => {
            if (!replaceable.properties["read only"]) {
              return (
                <ReplaceableField
                  key={replaceable.id}
                  replaceableId={replaceable.id}
                  onClick={replaceable.type !== "text" ? openDialog : () => {}}
                  updateReplaceableValue={
                    isAutofillFillSelector(replaceable)
                      ? updateAutofillValues
                      : updateReplaceableValue
                  }
                />
              );
            }
            return "";
          })}
        </ReplaceableContent>
        <Handle ref={ref => (this.previewHandle = ref)} />
        <ReplaceableContent
          columnsResized={columnsResized}
          ref={ref => (this.assetPreviewColumn = ref)}
          style={{
            minWidth:
              previewColumnWidth && columnsResized
                ? `${previewColumnWidth}px`
                : "100px"
          }}
          noBorder
        >
          <AssetPreview />
        </ReplaceableContent>
      </ReplaceablesContainer>
    );
  }
  render() {
    if (this.props.customPanel && this.props.customPanel.url) {
      return this.renderCustomPanelView();
    } else if (this.props.verticalLayout) {
      return this.renderVerticalView();
    }
    return this.renderDefaultView();
  }
}

AssetEditor.defaultProps = {
  openDialog: () => {},
  autofillData: {},
  verticalLayout: false,
  customPanel: null,
  previewImg: null,
  active: false,
  primeStyle: null
};

AssetEditor.propTypes = {
  autoPreviewEnabled: PropTypes.bool.isRequired,
  column1Replaceables: PropTypes.arrayOf(LuciPropTypes.Replaceable).isRequired,
  column2Replaceables: PropTypes.arrayOf(LuciPropTypes.Replaceable).isRequired,
  templateSwapping: PropTypes.bool.isRequired,
  openDialog: PropTypes.func,
  generatePreview: PropTypes.func.isRequired,
  updateReplaceableValue: PropTypes.func.isRequired,
  updateAutofillValues: PropTypes.func.isRequired,
  selectReplaceable: PropTypes.func.isRequired,
  // eslint-disable-next-line
  autofillData: PropTypes.any,
  verticalLayout: PropTypes.bool,
  customPanel: LuciPropTypes.CustomPanel,
  // eslint-disable-next-line
  previewImg: PropTypes.string,
  primeStyle: PropTypes.string
};

AssetEditor.contextTypes = {
  router: PropTypes.object.isRequired
};

const compareDisplayIndex = (a, b) => {
  if (
    a.properties.displayIndex < b.properties.displayIndex ||
    a.properties["display index"] < b.properties["display index"]
  ) {
    return -1;
  }

  if (
    a.properties.displayIndex > b.properties.displayIndex ||
    a.properties["display index"] > b.properties["display index"]
  ) {
    return 1;
  }

  return 0;
};

const compareLuciPanelReplaceables = (a, b) => {
  if (a.x < b.x) {
    return -1;
  }

  if (a.x > b.x) {
    return 1;
  }

  if (a.y < b.y) {
    return -1;
  }

  if (a.y > b.y) {
    return 1;
  }

  return 0;
};

const updateColumnDisplayIndex = (
  origReplaceable,
  colReplaceables,
  displayColumn
) => {
  const colIndex = colReplaceables.findIndex(
    vr => vr.id.replace("_replaceable", "") === origReplaceable.id
  );
  if (colIndex > -1) {
    return {
      ...origReplaceable,
      properties: {
        ...origReplaceable.properties,
        displayColumn,
        displayColumnOriginal: displayColumn,
        displayIndex: colIndex + 1,
        displayIndexOriginal: colIndex + 1
      }
    };
  }

  return null;
};

const updateReplaceablesFromLuciPanel = (allReplaceables, luciPanelItems) => {
  // set column replaceables based on luci panel
  const column1 = luciPanelItems.filter(r => r.x < 300);
  column1.sort(compareLuciPanelReplaceables);
  const column2 = luciPanelItems.filter(r => r.x >= 300);
  column2.sort(compareLuciPanelReplaceables);
  let col1HiddenCount = 0;
  let col2HiddenCount = 0;
  return allReplaceables.map(r => {
    let updatedReplaceable = updateColumnDisplayIndex(r, column1, "1");
    if (!updatedReplaceable) {
      updatedReplaceable = updateColumnDisplayIndex(r, column2, "2");
    }

    if (updatedReplaceable) {
      return updatedReplaceable;
    }

    let displayColumn = "1";
    let displayIndex = 0;
    if (r.type === "text") {
      displayColumn = "1";
      displayIndex = column1.length + 1 + col1HiddenCount;
      col1HiddenCount += 1;
    } else {
      displayColumn = "2";
      displayIndex = column2.length + 1 + col2HiddenCount;
      col2HiddenCount += 1;
    }

    return {
      ...r,
      properties: {
        ...r.properties,
        displayColumn,
        displayColumnOriginal: displayColumn,
        displayIndex,
        displayIndexOriginal: displayIndex
      }
    };
  });
};

const mapStateToProps = state => {
  const {
    mediaReplaceables,
    textReplaceables
  } = state.asset.templateDefinition;
  let column1Replaceables = [];
  let column2Replaceables = [];
  if (
    state.luciPanel &&
    state.luciPanel.panel &&
    textReplaceables &&
    mediaReplaceables
  ) {
    const allReplaceables = textReplaceables.concat(mediaReplaceables);
    const replaceables = updateReplaceablesFromLuciPanel(
      allReplaceables,
      state.luciPanel.panel.items
    );
    column1Replaceables = replaceables.filter(
      r => r.properties.displayColumn === "1"
    );
    column2Replaceables = replaceables.filter(
      r => r.properties.displayColumn === "2"
    );
  } else if (mediaReplaceables != null && textReplaceables != null) {
    column1Replaceables = textReplaceables
      .filter(r => r.properties.displayColumn === "1")
      .concat(
        mediaReplaceables.filter(r => r.properties.displayColumn === "1")
      );
    column2Replaceables = textReplaceables
      .filter(r => r.properties.displayColumn === "2")
      .concat(
        mediaReplaceables.filter(r => r.properties.displayColumn === "2")
      );
    if (column1Replaceables.length === 0 && column2Replaceables.length === 0) {
      column1Replaceables = textReplaceables;
      column2Replaceables = mediaReplaceables;
    }
  }
  const getConfigValue = makeGetConfigValue();
  return {
    autoPreviewEnabled:
      getConfigValue(state, "userConfig", "autoPreviewEnabled", "false") ===
      "true",
    customPanel: state.asset.templateDefinition.customPanel,
    column1Replaceables: column1Replaceables.sort(compareDisplayIndex),
    column2Replaceables: column2Replaceables.sort(compareDisplayIndex),
    templateSwapping: state.asset.templateSwapping,
    autofillData: state.asset.autofillColumnData,
    autofillLoading: state.asset.autofillLoading,
    generatingPreview: state.asset.generatingPreview,
    previewImg: state.asset.asset.previewImg,
    primeStyle: state.asset.primeStyle
  };
};

const mapActionCreators = {
  generatePreview: assetActions.generatePreview,
  updateReplaceableValue: assetActions.updateReplaceableValue,
  updateAutofillValues: assetActions.updateAutofillValues,
  selectReplaceable: assetActions.selectReplaceable
};

export default connect(mapStateToProps, mapActionCreators)(AssetEditor);



// WEBPACK FOOTER //
// ./app/components/Editor/AssetEditor.js